// XML plumbing shared by the GPX parser and the KML importer (no Swift counterpart: Swift uses Foundation's streaming
// `XMLParser`). The browser has no streaming XML parser, so documents are parsed with `DOMParser` and replayed in
// document order as start-element / characters / end-element events (`replay`), with source positions recomputed
// from the text where Swift reports line numbers.

/** Input bytes that are invalid in their encoding (libxml2: "Input is not proper UTF-8"). */
export class XMLInputError extends Error {
  constructor(
    readonly line: number,
    message: string,
  ) {
    super(message);
    this.name = 'XMLInputError';
  }
}

/** Local part of a qualified name (`gx:Track` → `Track`). */
export function localName(qualified: string): string {
  const colon = qualified.lastIndexOf(':');
  return colon < 0 ? qualified : qualified.slice(colon + 1);
}

// MARK: Input decoding

export function asBytes(data: Uint8Array | ArrayBuffer): Uint8Array {
  // `ArrayBuffer.isView` and the explicit view work across realms (jsdom tests, Node Buffers).
  if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
  return new Uint8Array(data);
}

/** Bytes of `s` in UTF-8 (lone surrogates count as U+FFFD, like `TextEncoder`), without encoding it. */
export function utf8ByteLength(s: string): number {
  let bytes = 0;
  for (let i = 0; i < s.length; i += 1) {
    const code = s.charCodeAt(i);
    if (code < 0x80) bytes += 1;
    else if (code < 0x800) bytes += 2;
    else if (code >= 0xd800 && code <= 0xdbff && i + 1 < s.length) {
      const next = s.charCodeAt(i + 1);
      if (next >= 0xdc00 && next <= 0xdfff) {
        bytes += 4;
        i += 1;
      } else {
        bytes += 3;
      }
    } else bytes += 3;
  }
  return bytes;
}

/** True when the input has something other than XML whitespace (space, tab, line feed, carriage return). */
export function containsNonXMLWhitespace(input: string | Uint8Array): boolean {
  for (let i = 0; i < input.length; i += 1) {
    const code = typeof input === 'string' ? input.charCodeAt(i) : input[i];
    if (code !== 0x20 && code !== 0x09 && code !== 0x0a && code !== 0x0d) return true;
  }
  return false;
}

/**
 * Decodes XML bytes like an XML parser: byte-order mark first, then the `encoding` of the XML declaration, else
 * UTF-8. Bytes that are invalid in that encoding are a well-formedness error (libxml2: "Input is not proper UTF-8").
 */
export function decodeXMLBytes(bytes: Uint8Array): string {
  const label = sniffEncoding(bytes);
  let decoder: TextDecoder;
  try {
    decoder = new TextDecoder(label, { fatal: true });
  } catch {
    decoder = new TextDecoder('utf-8', { fatal: true });
  }
  try {
    return decoder.decode(bytes);
  } catch {
    const lenient = new TextDecoder(decoder.encoding).decode(bytes);
    const firstInvalid = lenient.indexOf('�');
    const line = firstInvalid < 0 ? 1 : positionOf(lenient, firstInvalid).line;
    throw new XMLInputError(line, `Input is not proper ${decoder.encoding} (invalid byte sequence)`);
  }
}

function sniffEncoding(bytes: Uint8Array): string {
  const [b0, b1, b2, b3] = [bytes[0], bytes[1], bytes[2], bytes[3]];
  if (b0 === 0xef && b1 === 0xbb && b2 === 0xbf) return 'utf-8';
  if (b0 === 0xff && b1 === 0xfe) return 'utf-16le';
  if (b0 === 0xfe && b1 === 0xff) return 'utf-16be';
  if (b0 === 0x3c && b1 === 0x00 && b2 === 0x3f && b3 === 0x00) return 'utf-16le';
  if (b0 === 0x00 && b1 === 0x3c && b2 === 0x00 && b3 === 0x3f) return 'utf-16be';
  let head = '';
  const end = Math.min(bytes.length, 512);
  for (let i = 0; i < end; i += 1) head += String.fromCharCode(bytes[i]);
  const declared = /^<\?xml[^>]*?\sencoding\s*=\s*["']([A-Za-z0-9._:-]+)["']/.exec(head);
  return declared ? declared[1].toLowerCase() : 'utf-8';
}

// MARK: DOMParser

const mozillaParserErrorNamespace = 'http://www.mozilla.org/newlayout/xml/parsererror.xml';
const xhtmlNamespace = 'http://www.w3.org/1999/xhtml';

export interface XMLErrorInfo {
  line?: number;
  column?: number;
  message: string;
}

export type ParsedXML =
  | { kind: 'document'; document: Document; source: string }
  | { kind: 'error'; error: XMLErrorInfo; source: string };

export function parseXMLText(text: string, client = 'XML parsing'): ParsedXML {
  const DOMParserType = globalThis.DOMParser;
  if (typeof DOMParserType !== 'function') {
    throw new Error(`${client} needs DOMParser (a browser, or jsdom in tests); none is available in this environment.`);
  }
  const parser = new DOMParserType();
  const first = parseOnce(parser, text);
  if (first.kind === 'document') return first;

  // libxml2 (Swift) recovers from undeclared namespace prefixes; declare them and try again.
  const patched = declareUndeclaredPrefixes(text);
  if (patched !== undefined) {
    const second = parseOnce(parser, patched);
    if (second.kind === 'document') return second;
  }
  return first;
}

function parseOnce(parser: DOMParser, text: string): ParsedXML {
  let document: Document;
  try {
    document = parser.parseFromString(text, 'application/xml');
  } catch (error) {
    return { kind: 'error', error: { message: String(error) }, source: text };
  }
  const errorElement = findParserError(document);
  if (!errorElement) return { kind: 'document', document, source: text };
  return { kind: 'error', error: describeParserError(errorElement, text), source: text };
}

/** The `<parsererror>` element browsers (and jsdom) put in the result of a failed parse. */
function findParserError(document: Document): Element | undefined {
  const candidates = document.getElementsByTagNameNS('*', 'parsererror');
  for (let i = 0; i < candidates.length; i += 1) {
    const element = candidates[i];
    const namespace = element.namespaceURI;
    if (namespace === mozillaParserErrorNamespace || namespace === xhtmlNamespace) return element;
    if (element === document.documentElement && namespace === null) return element;
  }
  return undefined;
}

/** Position and text of a parse error, read from each engine's diagnostic format. */
function describeParserError(element: Element, source: string): XMLErrorInfo {
  const raw = (element.textContent ?? '').trim();
  let line: number | undefined;
  let column: number | undefined;
  let detail: string;
  let match: RegExpExecArray | null;
  if ((match = /error on line (\d+) at column (\d+):\s*([^\n]*)/i.exec(raw))) {
    // Chromium and WebKit (libxml2): "error on line 11 at column 14: Opening and ending tag mismatch: …".
    [line, column, detail] = [Number(match[1]), Number(match[2]), match[3].trim()];
  } else if ((match = /Line Number (\d+), Column (\d+)/i.exec(raw))) {
    // Firefox: "XML Parsing Error: mismatched tag. Expected: </trkpt>.\nLocation: …\nLine Number 11, Column 7:".
    [line, column] = [Number(match[1]), Number(match[2])];
    detail = firstLine(raw).replace(/^XML Parsing Error:\s*/i, '');
  } else if ((match = /^(\d+):(\d+):\s*([^\n]*)/.exec(raw))) {
    // jsdom (saxes): "11:13: unexpected close tag.".
    [line, column, detail] = [Number(match[1]), Number(match[2]), match[3].trim()];
  } else {
    detail = firstLine(raw);
  }
  if (detail === '') detail = 'unknown XML error';
  if (line !== undefined && !(line >= 1)) line = undefined;
  const tag = line === undefined ? undefined : tagNear(source, line, column);
  return { line, column, message: tag === undefined ? detail : `${detail} (at ${tag})` };
}

function firstLine(s: string): string {
  const end = s.search(/[\r\n]/);
  return (end < 0 ? s : s.slice(0, end)).trim();
}

/** The tag (`<name>` or `</name>`) at or before `column` on `line` of the source, for diagnostics. */
function tagNear(source: string, line: number, column: number | undefined): string | undefined {
  const lineText = sourceLine(source, line);
  if (lineText === undefined || lineText === '') return undefined;
  const index = Math.min(Math.max(column ?? lineText.length, 1), lineText.length) - 1;
  const open = lineText.lastIndexOf('<', index);
  if (open < 0) return undefined;
  const match = /^<\/?[^\s<>/]+/.exec(lineText.slice(open, open + 200));
  return match ? `${match[0]}>` : undefined;
}

/**
 * Swift's streaming parsers reject a wrong root (`notGPX`, "XML root is not <kml>") as soon as the root start tag is
 * read, even when the document is malformed further on. True when the source's first start tag is not
 * `<expectedLocalName>` and ends before the reported error position.
 */
export function rootIsNotBeforeError(source: string, error: XMLErrorInfo, expectedLocalName: string): boolean {
  if (error.line === undefined) return false;
  const root = findStartTag(source, 0);
  if (!root) return false;
  const name = /^<([^\s<>/]+)/.exec(source.slice(root.open, root.open + 200))?.[1];
  if (name === undefined || localName(name) === expectedLocalName) return false;
  const end = positionOf(source, root.close);
  return end.line < error.line || (end.line === error.line && error.column !== undefined && end.column < error.column);
}

const reservedPrefixes = new Set(['xml', 'xmlns']);
const ncName = /^[A-Za-z_À-￿][\w.\-·À-￿]*$/;

/** The source with `xmlns:<prefix>` declared on the root for every prefix it does not declare, if any. */
function declareUndeclaredPrefixes(text: string): string | undefined {
  const root = findStartTag(text, 0);
  if (!root) return undefined;
  const rootTag = text.slice(root.open, root.close + 1);
  const rootName = /^<[^\s<>/]+/.exec(rootTag);
  if (!rootName) return undefined;
  const declared = new Set<string>();
  for (const match of rootTag.matchAll(/\sxmlns:([^\s=/>]+)\s*=/g)) declared.add(match[1]);
  const used = new Set<string>();
  for (const match of text.matchAll(/<\/?([^\s<>/:!?="'&]+):[^\s<>/=:]/g)) used.add(match[1]);
  for (const match of text.matchAll(/\s([^\s<>/:!?="'&]+):[^\s<>/=:"']+\s*=\s*["']/g)) used.add(match[1]);
  const missing = [...used].filter(
    (prefix) => !declared.has(prefix) && !reservedPrefixes.has(prefix) && ncName.test(prefix),
  );
  if (missing.length === 0) return undefined;
  const declarations = missing
    .map((prefix) => ` xmlns:${prefix}="urn:x-trailbivvy:undeclared-prefix:${prefix}"`)
    .join('');
  const insertAt = root.open + rootName[0].length;
  return text.slice(0, insertAt) + declarations + text.slice(insertAt);
}

// MARK: DOM replay

const ELEMENT_NODE = 1;
const TEXT_NODE = 3;
const CDATA_SECTION_NODE = 4;

/** Receiver of the SAX-like events of `replay` (Swift `XMLParserDelegate`). */
export interface XMLEventHandler {
  /** `elementName` is the qualified name; `elementIndex` the document-order index of the start tag (root = 0). */
  didStartElement(elementName: string, attribute: (name: string) => string | undefined, elementIndex: number): void;
  didEndElement(elementName: string): void;
  /** Text and CDATA content. */
  foundCharacters(characters: string): void;
  /** True once the handler wants no more events (Swift `abortParsing`). */
  readonly isStopped: boolean;
}

/** Replays the subtree of `root` as SAX events in document order; stops as soon as the handler is stopped. */
export function replay(root: Element, handler: XMLEventHandler): void {
  let elementIndex = 0;
  const enter = (node: Node): void => {
    if (node.nodeType === ELEMENT_NODE) {
      const element = node as Element;
      handler.didStartElement(element.nodeName, (name) => element.getAttribute(name) ?? undefined, elementIndex);
      elementIndex += 1;
    } else if (node.nodeType === TEXT_NODE || node.nodeType === CDATA_SECTION_NODE) {
      handler.foundCharacters((node as CharacterData).data);
    }
  };

  // Iterative pre-order walk: no recursion limit on deeply nested input.
  let node: Node = root;
  enter(node);
  while (!handler.isStopped) {
    const child = node.nodeType === ELEMENT_NODE ? node.firstChild : null;
    if (child) {
      node = child;
      enter(node);
      continue;
    }
    for (;;) {
      if (node.nodeType === ELEMENT_NODE) handler.didEndElement(node.nodeName);
      if (handler.isStopped || node === root) return;
      const sibling: Node | null = node.nextSibling;
      if (sibling) {
        node = sibling;
        enter(node);
        break;
      }
      const parent = node.parentNode;
      if (!parent) return;
      node = parent;
    }
  }
}

// MARK: Source positions

/** Offsets of the `index`-th start tag of the source (document order, root = 0): its `<` and its closing `>`. */
export function findStartTag(text: string, index: number): { open: number; close: number } | undefined {
  let position = 0;
  let count = 0;
  for (;;) {
    const open = text.indexOf('<', position);
    if (open < 0) return undefined;
    const next = text.charCodeAt(open + 1);
    if (text.startsWith('<!--', open)) {
      position = skipPast(text, '-->', open + 4);
    } else if (text.startsWith('<![CDATA[', open)) {
      position = skipPast(text, ']]>', open + 9);
    } else if (next === 0x3f /* ? */) {
      position = skipPast(text, '?>', open + 2);
    } else if (next === 0x21 /* ! */) {
      position = skipDeclaration(text, open + 2);
    } else if (next === 0x2f /* / */) {
      position = open + 2;
    } else {
      if (count === index) return { open, close: endOfStartTag(text, open + 1) };
      count += 1;
      // `<` cannot occur inside a start tag (not even in attribute values), so the next `<` is the next markup.
      position = open + 1;
    }
  }
}

function skipPast(text: string, terminator: string, from: number): number {
  const found = text.indexOf(terminator, from);
  return found < 0 ? text.length : found + terminator.length;
}

/** Offset of the `>` that closes a start tag, skipping quoted attribute values. */
function endOfStartTag(text: string, from: number): number {
  let quote = 0;
  for (let i = from; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (quote !== 0) {
      if (code === quote) quote = 0;
    } else if (code === 0x22 || code === 0x27) {
      quote = code;
    } else if (code === 0x3e) {
      return i;
    }
  }
  return Math.max(text.length - 1, 0);
}

/** End of a `<!…>` declaration (DOCTYPE with its internal subset), skipping quotes and comments. */
function skipDeclaration(text: string, from: number): number {
  let depth = 0;
  let quote = 0;
  for (let i = from; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    if (quote !== 0) {
      if (code === quote) quote = 0;
    } else if (code === 0x22 || code === 0x27) {
      quote = code;
    } else if (code === 0x3c && text.startsWith('<!--', i)) {
      i = skipPast(text, '-->', i + 4) - 1;
    } else if (code === 0x5b) {
      depth += 1;
    } else if (code === 0x5d) {
      depth -= 1;
    } else if (code === 0x3e && depth <= 0) {
      return i + 1;
    }
  }
  return text.length;
}

/** 1-based line and column of `offset` (LF, CR LF and lone CR end a line, as in XML end-of-line handling). */
export function positionOf(text: string, offset: number): { line: number; column: number } {
  let line = 1;
  let lineStart = 0;
  const end = Math.min(offset, text.length);
  for (let i = 0; i < end; i += 1) {
    const code = text.charCodeAt(i);
    if (code === 0x0a || (code === 0x0d && text.charCodeAt(i + 1) !== 0x0a)) {
      line += 1;
      lineStart = i + 1;
    }
  }
  return { line, column: offset - lineStart + 1 };
}

/** Line of the `>` ending the `index`-th start tag (libxml2's line in its start-element callback); 1 if unknown. */
export function startTagLine(text: string, index: number): number {
  const tag = findStartTag(text, index);
  return tag ? positionOf(text, tag.close).line : 1;
}

/** Text of 1-based `line` of the source, without its line break. */
function sourceLine(text: string, line: number): string | undefined {
  let current = 1;
  let start = 0;
  while (current < line && start < text.length) {
    const code = text.charCodeAt(start);
    if (code === 0x0a || (code === 0x0d && text.charCodeAt(start + 1) !== 0x0a)) current += 1;
    start += 1;
  }
  if (current < line) return undefined;
  let end = start;
  while (end < text.length && text.charCodeAt(end) !== 0x0a && text.charCodeAt(end) !== 0x0d) end += 1;
  return text.slice(start, end);
}
